const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** The CSRF cookie is readable on purpose; the session cookie is not. */
const csrfToken = () =>
  document.cookie
    .split("; ")
    .find((c) => c.startsWith("wbm_csrf="))
    ?.split("=")[1] ?? "";

async function request(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const headers = { Accept: "application/json", "Content-Type": "application/json", ...(options.headers || {}) };
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) headers["X-CSRF-Token"] = csrfToken();
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    ...options,
    headers,
  });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch (e) {
      body = null;
    }
    const err = new Error(body?.userMessage || "Request failed");
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return res.json();
}

export const getConfig = () => request("/config");
export const getLocations = () => request("/locations");
export const getFleet = (locationId, date) =>
  request(`/fleet?locationId=${encodeURIComponent(locationId)}&date=${encodeURIComponent(date)}`);
export const getCatalog = (locationId) => request(`/catalog?locationId=${encodeURIComponent(locationId)}`);
export const getBoat = (assetId, locationId, date) =>
  request(`/boats/${assetId}?locationId=${encodeURIComponent(locationId)}&date=${encodeURIComponent(date)}`);
export const postQuote = (locationId, lines) =>
  request("/quote", { method: "POST", body: JSON.stringify({ locationId, lines }) });
export const postContact = (payload) =>
  request("/contact", { method: "POST", body: JSON.stringify(payload) });

export const createOrder = (payload) =>
  request("/orders", { method: "POST", body: JSON.stringify(payload) });
export const lookupBooking = (email, confirmationCode) =>
  request("/bookings/lookup", { method: "POST", body: JSON.stringify({ email, confirmationCode }) });
export const getGuestSession = () => request("/bookings/session");

export const createPaymentSession = (confirmationCode, email) =>
  request("/payments/session", { method: "POST", body: JSON.stringify({ confirmationCode, email }) });
/** Pay an outstanding balance from a signed-in session — no confirmation code. */
export const createAccountPaymentSession = (bookingId) =>
  request("/payments/account-session", { method: "POST", body: JSON.stringify({ bookingId }) });
/** The return-from-Checkout read. The platform resolves the payment INLINE on
 *  this first read, so it is called exactly ONCE — there is no polling. */
export const paymentReturn = (confirmationCode, email) =>
  request("/payments/return", { method: "POST", body: JSON.stringify({ confirmationCode, email }) });
/** Landed on our cancel URL: release the unpaid hold so the boat frees up. */
export const abandonOrder = (confirmationCode, email) =>
  request("/payments/abandon", { method: "POST", body: JSON.stringify({ confirmationCode, email }) });
/** The platform's lookup requires the consumer's email as an INPUT, so it is
 *  supplied per request and never stored server-side. */
export const getOrderStatus = (confirmationCode, email) =>
  request("/orders/status", { method: "POST", body: JSON.stringify({ confirmationCode, email }) });

// --- customer accounts (the customer token stays server-side) ---
export const getSession = () => request("/account/session");
export const accountRegister = (payload) =>
  request("/account/register", { method: "POST", body: JSON.stringify(payload) });
export const accountLogin = (email, password) =>
  request("/account/login", { method: "POST", body: JSON.stringify({ email, password }) });
export const accountLogout = () => request("/account/logout", { method: "POST" });
export const getProfile = () => request("/account/profile");
export const updateProfile = (payload) =>
  request("/account/profile", { method: "PUT", body: JSON.stringify(payload) });
export const getTrips = (skip = 0, take = 10) =>
  request(`/account/trips?skip=${skip}&take=${take}`);
export const getAccountOrder = (bookingId) => request(`/account/orders/${bookingId}`);
export const requestPasswordReset = (email) =>
  request("/account/password-reset", { method: "POST", body: JSON.stringify({ email }) });
export const confirmPasswordReset = (token, newPassword) =>
  request("/account/password-reset/confirm", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });

/** Live National Weather Service conditions for a marina. `available: false`
 *  from the BFF means the banner hides itself -- never a placeholder. */
export const getWeather = (location) => request(`/weather?location=${encodeURIComponent(location)}`);

/** Media served by our own origin (the BFF proxies vendor-hosted imagery). */
export const mediaUrl = (path) => (path && path.startsWith("/api/") ? `${BACKEND_URL}${path}` : path);
